import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  ConfirmationResult,
  RecaptchaVerifier,
  User,
  createUserWithEmailAndPassword,
  getRedirectResult,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signInWithPopup,
  signInWithRedirect,
  signOut
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { upsertUserProfileFromAuth } from '../services/userService';

interface AuthContextType {
  currentUser: User | null;
  isNewUser: boolean;
  clearNewUserFlag: () => void;
  login: (nextPath?: string) => Promise<void>;
  startPhoneLogin: (phoneNumberE164: string, recaptchaContainerId: string) => Promise<void>;
  verifyPhoneCode: (code: string) => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  // Hold the active reCAPTCHA verifier so a resend can dispose the previous one
  // before rendering a new widget (Firebase throws if two render in one element).
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);

  const clearNewUserFlag = () => setIsNewUser(false);

  const login = async (nextPath?: string) => {
    try {
      // Persist the post-login target so it survives the OAuth redirect roundtrip.
      if (nextPath) {
        try { sessionStorage.setItem('sm_post_login_next', nextPath); } catch {}
      }

      // Popup-first (works on custom domains where signInWithRedirect can hang
      // due to cross-origin iframe handshake). Falls back to redirect only if
      // the popup is explicitly blocked.
      try {
        const cred = await signInWithPopup(auth, googleProvider);
        setCurrentUser(cred.user);
      } catch (popupError: any) {
        const code = popupError?.code;
        if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
          // User dismissed the popup — surface as a recoverable error.
          throw new Error('Sign-in was cancelled. Please try again.');
        }
        if (code === 'auth/popup-blocked') {
          await signInWithRedirect(auth, googleProvider);
          return;
        }
        throw popupError;
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const startPhoneLogin = async (phoneNumberE164: string, recaptchaContainerId: string) => {
    // Dispose any verifier from a previous send/resend so we don't render a
    // second reCAPTCHA into the same container (Firebase throws otherwise).
    if (recaptchaVerifierRef.current) {
      try { recaptchaVerifierRef.current.clear(); } catch { /* already cleared */ }
      recaptchaVerifierRef.current = null;
    }
    const container = document.getElementById(recaptchaContainerId);
    if (container) container.innerHTML = '';

    const verifier = new RecaptchaVerifier(auth, recaptchaContainerId, {
      size: 'invisible'
    });
    recaptchaVerifierRef.current = verifier;
    try {
      const result = await signInWithPhoneNumber(auth, phoneNumberE164, verifier);
      setConfirmationResult(result);
    } catch (e) {
      // Reset on failure so the next attempt starts from a clean widget.
      try { verifier.clear(); } catch { /* noop */ }
      recaptchaVerifierRef.current = null;
      throw e;
    }
  };

  const verifyPhoneCode = async (code: string) => {
    if (!confirmationResult) throw new Error('Please request an OTP first.');
    const cred = await confirmationResult.confirm(code);
    setCurrentUser(cred.user);
    setConfirmationResult(null);
  };

  const loginWithEmail = async (email: string, password: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) throw new Error('Email is required');
    if (!password) throw new Error('Password is required');
    const cred = await signInWithEmailAndPassword(auth, normalizedEmail, password);
    setCurrentUser(cred.user);
  };

  const signUpWithEmail = async (email: string, password: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) throw new Error('Email is required');
    if (!password || password.length < 6) throw new Error('Password must be at least 6 characters');
    const cred = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
    setCurrentUser(cred.user);
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  useEffect(() => {
    // Handle redirect result
    getRedirectResult(auth).then((result) => {
      if (result?.user) {
        setCurrentUser(result.user);
      }
    }).catch((error) => {
      console.error('Redirect result error:', error);
    });

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const { isNew } = await upsertUserProfileFromAuth(user);
          if (isNew) setIsNewUser(true);
        } catch {
          // non-fatal
        }
      } else {
        setIsNewUser(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    isNewUser,
    clearNewUserFlag,
    login,
    startPhoneLogin,
    verifyPhoneCode,
    loginWithEmail,
    signUpWithEmail,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
